import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import client from 'utils/client';
import { useToast } from 'components/Toast/useToast';
import { formatCurrency, formatThousand, formatAxisLabel } from 'utils/helpers';
import ReportDialog from 'pages/transactions/ReportDialog';
import { useAuth } from 'contexts/AuthContext';
import PerformanceTable from './PerformanceTable';
import './dashboard-charts.css';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Today', short: 'Today' },
  { value: 'weekly', label: 'This week', short: 'Week' },
  { value: 'monthly', label: 'This month', short: 'Month' },
  { value: '3months', label: 'Last 3 months', short: '3M' },
  { value: '6months', label: 'Last 6 months', short: '6M' },
  { value: '1year', label: 'This year', short: 'YTD' },
];

const BRAND = '#1e3a8a';
const BRAND_MID = '#3b5bb5';
const BRAND_SOFT = '#93a7e0';
const PIE_COLORS = [BRAND, BRAND_SOFT, BRAND_MID];

function ChartTooltip({ active, payload, formatter, suffix }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const raw = p.value;
  const value = formatter ? formatter(raw) : raw;
  return (
    <div className='chart-tooltip'>
      <span className='chart-tooltip-bar' style={{ background: p.payload?.fill || p.color || BRAND }} />
      <div>
        <div className='chart-tooltip-value tabular'>{value}</div>
        {suffix ? <div className='chart-tooltip-sub'>{suffix}</div> : null}
      </div>
    </div>
  );
}

const BUCKET_FORMAT = {
  hour: 'HH:mm',
  day: 'MMM D',
  week: 'MMM D',
  month: 'MMM YYYY',
};

const formatBucket = (value, granularity) => {
  if (!value) return '';
  const fmt = BUCKET_FORMAT[granularity] || 'MMM D';
  return dayjs(value).format(fmt);
};

const PERIOD_LABEL = {
  daily: 'today',
  weekly: 'this week',
  monthly: 'this month',
  '3months': 'last 3 months',
  '6months': 'last 6 months',
  '1year': 'this year',
};

function Kpi({ label, value, sub, subTone, to, primary }) {
  const card = (
    <div className={`kpi-card ${primary ? 'kpi-card--primary' : ''}`}>
      <div className='kpi-label'>{label}</div>
      <div className='kpi-value tabular'>{value}</div>
      {sub ? <div className={`kpi-sub ${subTone ? `kpi-sub--${subTone}` : ''}`}>{sub}</div> : null}
    </div>
  );
  return to ? <Link to={to} className='kpi-link'>{card}</Link> : card;
}

function DashboardCharts() {
  const [terminalData, setTerminalData] = useState([]);
  const [txnBreakdown, setTxnBreakdown] = useState([]);
  const [timeseries, setTimeseries] = useState({ granularity: 'day', points: [] });
  const [topMerchants, setTopMerchants] = useState([]);
  const [lowMerchants, setLowMerchants] = useState([]);
  const [topTerminals, setTopTerminals] = useState([]);
  const [lowTerminals, setLowTerminals] = useState([]);
  const [counts, setCounts] = useState({ merchants: 0, transactions: 0 });
  const [period, setPeriod] = useState('daily');
  const [metric, setMetric] = useState('value');
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const toast = useToast();
  const { isMerchant } = useAuth();

  useEffect(() => {
    const safe = (p) => p.then((r) => r.data).catch(() => null);
    const requests = [safe(client.get('/transactions/count'))];
    if (!isMerchant) requests.unshift(safe(client.get('/merchants/count')));
    Promise.all(requests).then((results) => {
      const t = isMerchant ? results[0] : results[1];
      const m = isMerchant ? null : results[0];
      setCounts({ merchants: m?.data ?? 0, transactions: t?.data ?? 0 });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCharts = async () => {
      setLoading(true);
      try {
        const merchantPerfRequests = isMerchant
          ? [Promise.resolve({ data: { data: [] } }), Promise.resolve({ data: { data: [] } })]
          : [
            client.get(`/transactions/merchant-performance?limit=5&type=high&period=${period}&metric=${metric}`),
            client.get(`/transactions/merchant-performance?limit=5&type=low&period=${period}&metric=${metric}`),
          ];
        const [terminalRes, seriesRes, topMerchantRes, lowMerchantRes, topTerminalRes, lowTerminalRes] = await Promise.all([
          client.get(`/terminals/status-summary?period=${period}`),
          client.get(`/transactions/timeseries?period=${period}`),
          merchantPerfRequests[0],
          merchantPerfRequests[1],
          client.get(`/transactions/terminal-performance?limit=5&type=high&period=${period}&metric=${metric}`),
          client.get(`/transactions/terminal-performance?limit=5&type=low&period=${period}&metric=${metric}`),
        ]);

        const terminalsData = terminalRes?.data?.data || {};
        setTerminalData([
          { name: 'Active', value: Number(terminalsData.active) || 0 },
          { name: 'Inactive', value: Number(terminalsData.inactive) || 0 },
        ]);

        // Donut totals are derived from the timeseries response — same indexed
        // range scan, no separate /summary call.
        const seriesData = seriesRes?.data?.data || { granularity: 'day', points: [] };
        const success = seriesData.points.reduce((s, p) => s + (Number(p.successCount) || 0), 0);
        const total = seriesData.points.reduce((s, p) => s + (Number(p.count) || 0), 0);
        setTxnBreakdown([
          { name: 'Successful', value: success },
          { name: 'Pending', value: Math.max(total - success, 0) },
        ]);
        setTimeseries(seriesData);

        setTopMerchants(topMerchantRes.data.data || []);
        setLowMerchants(lowMerchantRes.data.data || []);
        setTopTerminals(topTerminalRes.data.data || []);
        setLowTerminals(lowTerminalRes.data.data || []);
        setLastUpdated(new Date());
      } catch (err) {
        const msg = err?.message || 'Failed to load dashboard data';
        try { toast.onOpen({ type: 'error', message: msg }); } catch (_) { /* no-op */ }
        setTerminalData([]);
        setTxnBreakdown([]);
        setTimeseries({ granularity: 'day', points: [] });
        setTopMerchants([]);
        setLowMerchants([]);
        setTopTerminals([]);
        setLowTerminals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCharts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, metric]);

  const activeCount = Number(terminalData?.[0]?.value) || 0;
  const inactiveCount = Number(terminalData?.[1]?.value) || 0;
  const totalTerminals = activeCount + inactiveCount;
  const totalTxn = (Number(txnBreakdown?.[0]?.value) || 0) + (Number(txnBreakdown?.[1]?.value) || 0);
  const periodLabel = PERIOD_LABEL[period] || period;

  return (
    <div className='dashboard'>
      <div className='dashboard-header'>
        <div className='dashboard-header-text'>
          <h1 className='dashboard-title'>Overview</h1>
          <p className='dashboard-subtitle'>
            Payments activity {periodLabel}
            {lastUpdated ? <span className='dashboard-updated'> · Updated {dayjs(lastUpdated).format('h:mm A')}</span> : null}
          </p>
        </div>
        <div className='dashboard-actions'>
          <div className='metric-toggle' role='group' aria-label='Metric'>
            <button
              type='button'
              className={`metric-toggle-btn ${metric === 'value' ? 'is-active' : ''}`}
              onClick={() => setMetric('value')}>
              ₦ Value
            </button>
            <button
              type='button'
              className={`metric-toggle-btn ${metric === 'count' ? 'is-active' : ''}`}
              onClick={() => setMetric('count')}>
              Count
            </button>
          </div>
          <button type='button' className='btn-secondary dashboard-export' onClick={() => setExportOpen(true)}>
            Export
          </button>
        </div>
      </div>

      <div className='period-pill' role='tablist' aria-label='Period'>
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type='button'
            role='tab'
            aria-selected={period === opt.value}
            className={`period-pill-btn ${period === opt.value ? 'is-active' : ''}`}
            onClick={() => setPeriod(opt.value)}>
            <span className='period-pill-long'>{opt.label}</span>
            <span className='period-pill-short'>{opt.short}</span>
          </button>
        ))}
      </div>

      {exportOpen ? (
        <ReportDialog onClose={() => setExportOpen(false)} defaultMode='report' filters={{ period }} />
      ) : null}

      <div className='kpi-row'>
        {!isMerchant ? (
          <Kpi label='Merchants' value={formatThousand(counts.merchants)} sub='All time' to='/merchants' />
        ) : null}
        <Kpi label='Terminals' value={formatThousand(totalTerminals)} sub={`${formatThousand(activeCount)} active`} subTone='success' to='/terminals' />
        <Kpi label='Transactions' value={formatThousand(counts.transactions)} sub='All time' to='/transactions' primary />
        <Kpi label={metric === 'value' ? `Volume ${periodLabel}` : `Count ${periodLabel}`} value={formatThousand(totalTxn)} sub={`${formatThousand(Number(txnBreakdown?.[0]?.value) || 0)} successful`} subTone='success' />
      </div>

      <div className='chart-row two-col'>
        <div className='chart-card chart-card--with-center'>
          <div className='card-head'>
            <div className='card-title'>Terminal status</div>
            <div className='card-hint'>{isMerchant ? 'Your active terminals' : 'Active vs inactive'}</div>
          </div>
          <div className='chart-with-center'>
            {loading ? (
              <div className='chart-loading'><div className='skeleton skeleton-circle' /></div>
            ) : (
              <>
                <ResponsiveContainer width='100%' height={220}>
                  <PieChart>
                    <Pie data={terminalData} dataKey='value' nameKey='name' innerRadius={62} outerRadius={90} stroke='none'>
                      {terminalData.map((_, i) => (<Cell key={`t-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                    </Pie>
                    <Tooltip cursor={false} content={<ChartTooltip formatter={formatThousand} suffix='Terminals' />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className='chart-center-overlay'>
                  <div className='chart-center-value tabular'>{formatThousand(totalTerminals)}</div>
                  <div className='chart-center-sub'>Terminals</div>
                </div>
              </>
            )}
          </div>
          <div className='legend-stacked'>
            <div className='legend-stack-item'>
              <div className='legend-stack-head'><span className='dot' style={{ background: PIE_COLORS[0] }} /><span>Active</span></div>
              <div className='legend-stack-value tabular'>{formatThousand(activeCount)}</div>
            </div>
            {!isMerchant ? (
              <div className='legend-stack-item'>
                <div className='legend-stack-head'><span className='dot' style={{ background: PIE_COLORS[1] }} /><span>Inactive</span></div>
                <div className='legend-stack-value tabular'>{formatThousand(inactiveCount)}</div>
              </div>
            ) : null}
          </div>
        </div>

        <div className='chart-card chart-card--with-center'>
          <div className='card-head'>
            <div className='card-title'>Transactions</div>
            <div className='card-hint'>Successful vs pending</div>
          </div>
          <div className='chart-with-center'>
            {loading ? (
              <div className='chart-loading'><div className='skeleton skeleton-circle' /></div>
            ) : totalTxn === 0 ? (
              <div className='chart-loading'><span className='card-hint'>No data for selected period</span></div>
            ) : (
              <>
                <ResponsiveContainer width='100%' height={220}>
                  <PieChart>
                    <Pie data={txnBreakdown} dataKey='value' nameKey='name' innerRadius={62} outerRadius={90} stroke='none'>
                      {txnBreakdown.map((_, i) => (<Cell key={`x-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                    </Pie>
                    <Tooltip cursor={false} content={<ChartTooltip formatter={formatThousand} suffix='Transactions' />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className='chart-center-overlay'>
                  <div className='chart-center-value tabular'>{formatThousand(totalTxn)}</div>
                  <div className='chart-center-sub'>Transactions</div>
                </div>
              </>
            )}
          </div>
          <div className='legend-stacked'>
            <div className='legend-stack-item'>
              <div className='legend-stack-head'><span className='dot' style={{ background: PIE_COLORS[0] }} /><span>Successful</span></div>
              <div className='legend-stack-value tabular'>{formatThousand(Number(txnBreakdown?.[0]?.value) || 0)}</div>
            </div>
            <div className='legend-stack-item'>
              <div className='legend-stack-head'><span className='dot' style={{ background: PIE_COLORS[1] }} /><span>Pending</span></div>
              <div className='legend-stack-value tabular'>{formatThousand(Number(txnBreakdown?.[1]?.value) || 0)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className='chart-row one-col'>
        <div className='chart-card'>
          <div className='card-head'>
            <div className='card-title'>{metric === 'value' ? 'Transaction volume' : 'Transaction count'}</div>
            <div className='card-hint'>By {timeseries.granularity}</div>
          </div>
          {loading ? (
            <div style={{ padding: '16px 12px' }}>
              <div className='skeleton skeleton-bar' style={{ width: '90%' }} />
              <div className='skeleton skeleton-bar' style={{ width: '70%' }} />
              <div className='skeleton skeleton-bar' style={{ width: '80%' }} />
              <div className='skeleton skeleton-bar' style={{ width: '60%' }} />
            </div>
          ) : timeseries.points.length === 0 ? (
            <div className='chart-loading'><span className='card-hint'>No data for selected period</span></div>
          ) : (
            <ResponsiveContainer width='100%' height={260}>
              <LineChart data={timeseries.points} margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid stroke='#eef2f7' vertical={false} />
                <XAxis
                  dataKey='bucket'
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(v) => formatBucket(v, timeseries.granularity)}
                  minTickGap={24}
                />
                <YAxis
                  tickFormatter={formatAxisLabel}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  width={56}
                />
                <Tooltip
                  cursor={{ stroke: BRAND, strokeWidth: 1, strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0];
                    const raw = p.value;
                    const label = formatBucket(p.payload?.bucket, timeseries.granularity);
                    const value = metric === 'value' ? formatCurrency(raw, 'NGN') : formatThousand(raw);
                    return (
                      <div className='chart-tooltip'>
                        <span className='chart-tooltip-bar' style={{ background: BRAND }} />
                        <div>
                          <div className='chart-tooltip-value tabular'>{value}</div>
                          <div className='chart-tooltip-sub'>{label}</div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Line
                  type='monotone'
                  dataKey={metric === 'value' ? 'amount' : 'count'}
                  stroke={BRAND}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, fill: BRAND, stroke: '#fff', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {!isMerchant ? (
        <div className='chart-row two-col'>
          <PerformanceTable title='Top merchants' data={topMerchants} loading={loading} metric={metric} />
          <PerformanceTable title='Low merchants' data={lowMerchants} loading={loading} metric={metric} />
        </div>
      ) : null}

      <div className='chart-row two-col'>
        <PerformanceTable title='Top terminals' data={topTerminals} loading={loading} metric={metric} displayField='terminal_id' />
        <PerformanceTable title='Low terminals' data={lowTerminals} loading={loading} metric={metric} displayField='terminal_id' />
      </div>
    </div>
  );
}

export default DashboardCharts;
